import React, { useState, useEffect } from 'react';
import { Box, Text, Stack, RingProgress, ThemeIcon, Paper, Group, Progress } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

const StepperLoader = ({ 
  title = "Verifying Identity Document",
  steps = [],
  stepDuration = 3000, // milliseconds between steps
  onComplete = () => {}
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [stepProgress, setStepProgress] = useState(0);
  const totalSteps = steps.length;

  useEffect(() => {
    // Reset progress when step changes
    setStepProgress(0);
    
    // Create progress animation
    const progressInterval = setInterval(() => {
      setStepProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + (100 / (stepDuration / 100)); // Update every 100ms
      });
    }, 100);

    if (currentStep < totalSteps - 1) {
      const timer = setTimeout(() => {
        setCompletedSteps(prev => [...prev, currentStep]);
        setCurrentStep(prev => prev + 1);
      }, stepDuration);

      return () => {
        clearTimeout(timer);
        clearInterval(progressInterval);
      };
    } else if (currentStep === totalSteps - 1) {
      // Complete the last step after the duration
      const timer = setTimeout(() => {
        setCompletedSteps(prev => [...prev, currentStep]);
        onComplete();
      }, stepDuration);

      return () => {
        clearTimeout(timer);
        clearInterval(progressInterval);
      };
    }
  }, [currentStep, totalSteps, stepDuration, onComplete]);

  const getStepStatus = (index) => {
    if (completedSteps.includes(index)) return 'completed';
    if (currentStep === index) return 'active';
    return 'pending';
  };

  const progressValue = ((currentStep + 1) / totalSteps) * 100;

  return (
    <>
      <style>
        {`
          @keyframes spin {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
        `}
      </style>
      <Paper 
        radius="xl" 
        p="xl" 
        style={{ 
          maxWidth: 450,
          margin: '0 auto'
        }}
      >
      <Stack spacing="xl" align="center">
        {/* Title */}
        <Text size="xl" weight={600} align="center">
          {title}
        </Text>

        {/* Circular Progress */}
        <Box style={{ position: 'relative' }}>
          {/* Background ring */}
          <RingProgress
            size={120}
            thickness={4}
            sections={[
              { value: 100, color: '#e8e8e8' }
            ]}
            style={{ position: 'absolute' }}
          />
          
          {/* Progress ring */}
          <RingProgress
            size={120}
            thickness={4}
            sections={[
              { value: progressValue, color: '#52c41a' }
            ]}
            roundCaps
            style={{ position: 'absolute' }}
          />
          
          {/* Animated spinning ring */}
          <Box
            style={{
              position: 'absolute',
              width: 120,
              height: 120,
              animation: 'spin 2s linear infinite'
            }}
          >
            <RingProgress
              size={120}
              thickness={2}
              sections={[
                { value: 25, color: '#1890ff' },
                { value: 75, color: 'transparent' }
              ]}
              roundCaps
            />
          </Box>
          
          <Box
            style={{
              position: 'relative',
              width: 120,
              height: 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box style={{ textAlign: 'center' }}>
              <Text size={28} weight={700}>
                {currentStep + 1}
              </Text>
              <Text size="xs" color="dimmed">
                of {totalSteps}
              </Text>
            </Box>
          </Box>
        </Box>

        {/* Steps List */}
        <Stack spacing="sm" style={{ width: '100%' }}>
          {steps.map((step, index) => {
            const status = getStepStatus(index);
            const isActive = status === 'active';
            const isCompleted = status === 'completed';
            const isPending = status === 'pending';

            return (
              <Paper
                key={index}
                p="md"
                radius="md"
                style={{
                  backgroundColor: isActive ? '#e6f7ff' : isCompleted ? '#f0f9ff' : '#ffffff',
                  border: isActive ? '1px solid #1890ff' : '1px solid #e8e8e8',
                  transition: 'all 0.3s ease'
                }}
              >
                <Group noWrap>
                  {/* Step Number or Check Icon */}
                  <ThemeIcon
                    size="lg"
                    radius="xl"
                    variant={isCompleted ? "filled" : isActive ? "light" : "default"}
                    color={isCompleted ? "green" : isActive ? "blue" : "gray"}
                  >
                    {isCompleted ? (
                      <IconCheck size={20} />
                    ) : (
                      <Text size="sm" weight={600}>
                        {index + 1}
                      </Text>
                    )}
                  </ThemeIcon>

                  {/* Step Content */}
                  <Box style={{ flex: 1 }}>
                    <Text 
                      size="sm" 
                      weight={600}
                      color={isPending ? "dimmed" : "dark"}
                    >
                      {step.title}
                    </Text>
                    <Text 
                      size="xs" 
                      color="dimmed"
                      style={{
                        opacity: isPending ? 0.6 : 1
                      }}
                    >
                      {step.description}
                    </Text>
                    {/* Progress bar for active step */}
                    {isActive && (
                      <Progress 
                        value={stepProgress} 
                        size="xs" 
                        color="blue" 
                        mt={8}
                        animate
                      />
                    )}
                  </Box>
                </Group>
              </Paper>
            );
          })}
        </Stack>

        {/* Bottom Progress Bar */}
        <Box style={{ width: '100%' }}>
          <Text size="xs" color="dimmed" align="center" mb="xs">
            Step {currentStep + 1} of {totalSteps}
          </Text>
          <Progress value={progressValue} size="sm" color="blue" />
        </Box>
      </Stack>
    </Paper>
    </>
  );
};

export default StepperLoader;